import { useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  InlineStack,
  Search,
} from '@customerio/pluma-components/react';
import { buildEmailSearchOptions, buildEmailSearchTerm } from './email-search-options';
import { EmailSearchPopover } from './email-search-popover';

/** The width the people list pages give the field beside their other controls. */
export const EMAIL_SEARCH_LIST_WIDTH = '256px';

type EmailSearchProps = {
  /** The committed search term from the URL — seeds the input. */
  term: string;
  /** Called with the final term (wildcard-wrapped when a suggestion was chosen). */
  onSearch: (term: string) => void;
  /**
   * How wide the input is. The caller decides: a list page gives it a fixed
   * size beside its other controls, a modal lets it fill the body.
   */
  width?: string;
  /** Put the cursor in the field on mount, where searching is the first thing to do. */
  autofocus?: boolean;
  /** Nothing to search for, because the viewer cannot act on a result. */
  isDisabled?: boolean;
};

/**
 * The email search input: a search field offering wildcard suggestions for
 * the typed term ("Search for emails that start with…"), with the search-tips
 * popover beside it. Submitting with a suggestion focused (arrow keys) or
 * clicking one wraps the term with that suggestion's wildcards.
 */
export function EmailSearch({
  term,
  onSearch,
  width = '100%',
  autofocus = false,
  isDisabled = false,
}: EmailSearchProps) {
  const [searchTerm, setSearchTerm] = useState(term);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  // Re-seed the input when the committed term changes from outside (mode
  // switches clear it; a back/forward navigation restores it).
  const [prevTerm, setPrevTerm] = useState(term);
  if (term !== prevTerm) {
    setPrevTerm(term);
    setSearchTerm(term);
  }

  const options = useMemo(() => buildEmailSearchOptions(searchTerm), [searchTerm]);
  const canShowDropdown = options.length > 0;
  const focusedOption = focusedIndex == null ? null : (options[focusedIndex] ?? null);

  // Fires on Enter and on clearing the field; a suggestion focused via the
  // arrow keys wraps the term with its wildcards.
  const commit = (value: string) => {
    onSearch(buildEmailSearchTerm(value, focusedOption?.id ?? null));
    setIsDropdownOpen(false);
    setFocusedIndex(null);
  };

  return (
    <InlineStack alignBlock="stretch" gap="100">
      <div style={{ width }}>
        <DropdownMenu
          isOpen={isDropdownOpen && canShowDropdown}
          onOpenChange={setIsDropdownOpen}
          listNavigationOptions={{
            activeIndex: focusedIndex,
            onNavigate: setFocusedIndex,
            virtual: true,
            loop: true,
          }}
          withTypeahead={false}
          initialFocus={-1}
          shouldReturnFocus={false}
          shouldTriggerOnFocus
          // Enter commits the search via the input's own handler; keep the
          // dropdown's click plugin off the keyboard so it can't double-handle
          // the key while a suggestion holds virtual focus.
          floatingPluginOptions={{ click: { keyboardHandlers: false } }}
        >
          {(context) => (
            <>
              <Search
                className="fs-mask"
                autofocus={autofocus}
                isDisabled={isDisabled}
                ariaLabel="Email address search"
                placeholder="Find by email address"
                value={searchTerm}
                onSearchInput={(value: string) => {
                  setSearchTerm(value);
                  setFocusedIndex(null);
                  if (buildEmailSearchOptions(value).length > 0) setIsDropdownOpen(true);
                }}
                onSearch={commit}
                shouldSearchOnEnter
                shouldSearchOnClear
                ref={context.reference.setReference}
                {...context.reference.getReferenceProps()}
              />
              <DropdownMenuContent>
                {options.map((option) => (
                  <DropdownMenuItem
                    key={option.id}
                    id={option.id}
                    onClick={() => onSearch(buildEmailSearchTerm(searchTerm, option.id))}
                  >
                    {option.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </>
          )}
        </DropdownMenu>
      </div>
      <InlineStack alignBlock="center">
        <EmailSearchPopover />
      </InlineStack>
    </InlineStack>
  );
}
